import { useState } from "react";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { Modal } from "@excalidraw/excalidraw/components/Modal";
import { Island } from "@excalidraw/excalidraw/components/Island";
import { CloseIcon } from "@excalidraw/excalidraw/components/icons";
import { t } from "@excalidraw/excalidraw/i18n";

type AuthView = "login" | "signup";

export const AuthModal = ({
  isOpen,
  onClose,
  onAuthSuccess,
  currentUser,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess?: (user: { id: number; username: string; token: string }) => void;
  currentUser?: { username: string; token: string } | null;
}) => {
  const { t } = useI18n();
  const [view, setView] = useState<AuthView>(currentUser?.username === "admin" ? "signup" : "login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const API_URL = import.meta.env.VITE_APP_API_URL || "/api";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = view === "signup" ? "/auth/signup" : "/auth/login";
      const body: any = { username, password };
      if (view === "signup") body.email = email;

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (view === "signup" && currentUser?.token) {
        headers["Authorization"] = `Bearer ${currentUser.token}`;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Authentication failed");
      }

      if (view === "signup") {
        alert("Usuario creado exitosamente");
        setUsername("");
        setPassword("");
        setEmail("");
        onClose();
      } else {
        onAuthSuccess?.({ ...data.user, token: data.token });
        onClose();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      labelledBy="auth-dialog-title"
      onCloseRequest={onClose}
      maxWidth={400}
    >
      <Island>
        <h2 id="auth-dialog-title" className="Dialog__title">
          <span className="Dialog__titleContent">
            {view === "signup" ? t("auth.signup") : t("auth.login")}
          </span>
        </h2>
        <button
          className="Dialog__close"
          onClick={onClose}
          title={t("buttons.close")}
          aria-label={t("buttons.close")}
          type="button"
        >
          {CloseIcon}
        </button>
        <div className="Dialog__content">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {error && (
              <div className="alert alert--danger" style={{ padding: "0.5rem" }}>
                {error}
              </div>
            )}

            <input
              type="text"
              placeholder={t("auth.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              style={{ padding: "0.5rem", fontSize: "1rem" }}
            />

            {view === "signup" && (
              <input
                type="email"
                placeholder={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ padding: "0.5rem", fontSize: "1rem" }}
              />
            )}

            <input
              type="password"
              placeholder={t("auth.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ padding: "0.5rem", fontSize: "1rem" }}
            />

            <button
              type="submit"
              disabled={loading}
              style={{ padding: "0.5rem 1rem", fontSize: "1rem", cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? t("auth.loading") : view === "signup" ? t("auth.signup") : t("auth.login")}
            </button>

            {(!currentUser || currentUser.username === "admin") && (
              <button
                type="button"
                onClick={() => setView(view === "login" ? "signup" : "login")}
                style={{ background: "none", border: "none", color: "var(--link-color)", cursor: "pointer", padding: 0 }}
              >
                {view === "login"
                  ? currentUser?.username === "admin" ? "Crear Nuevo Usuario" : ""
                  : t("auth.hasAccount")}
              </button>
            )}
          </form>
        </div>
      </Island>
    </Modal>
  );
};